import axios from "axios";
import * as config from "@/config";

const api = config.apiUrl;

/**
 * Updates afvalstof data for a specific afvalregister item
 * @param {string} arId - The afvalregister ID
 * @param {number} hoevKg - The quantity in kg
 * @returns {Promise<Object>} - API response data
 */
export const updateAfvalstof = async (arId, hoevKg) => {
    try {
        // Validate required parameters
        if (!arId || hoevKg === null || hoevKg === undefined) {
            throw new Error('Missing required parameters: arId and hoevKg are required');
        }

        // Ensure hoevKg is a valid number
        const parsedHoevKg = parseFloat(hoevKg);
        if (isNaN(parsedHoevKg)) {
            throw new Error('hoevKg must be a valid number');
        }

        const putObject = {
            hoevKg: parsedHoevKg
        };

        console.log(`Updating afvalstof for arId ${arId}:`, putObject);

        const response = await axios.put(
            `${api}/afvalregister/${arId}`, 
            putObject
        );

        console.log('Afvalstof update response:', response.data);
        return response.data;

    } catch (error) {
        console.error('Error updating afvalstof:', error);
        
        // Re-throw with more context
        if (error.response) {
            // Server responded with error status
            const serverError = new Error(`Server error: ${error.response.status} - ${error.response.statusText}`);
            serverError.response = error.response;
            throw serverError;
        } else if (error.request) {
            // Request was made but no response received
            throw new Error('Network error: No response received from server');
        } else {
            // Something else happened
            throw error;
        }
    }
};

/**
 * Batch update multiple afvalstof items
 * @param {Array} afvalstofUpdates - Array of {aRId, hoevKg} objects
 * @returns {Promise<Array>} - Array of API responses
 */
export const updateMultipleAfvalstof = async (afvalstofUpdates) => {
    try {
        if (!Array.isArray(afvalstofUpdates) || afvalstofUpdates.length === 0) {
            throw new Error('Missing required parameter: afvalstofUpdates array is required');
        }

        console.log(`Batch updating ${afvalstofUpdates.length} afvalstof items`);

        // Execute all updates in parallel
        const updatePromises = afvalstofUpdates.map(update => 
            updateAfvalstof(update.aRId, update.hoevKg)
        );

        const results = await Promise.allSettled(updatePromises);
        
        // Check for any failures
        const failures = results.filter(result => result.status === 'rejected');
        if (failures.length > 0) {
            console.warn(`${failures.length} out of ${results.length} afvalstof updates failed`);
            failures.forEach((failure, index) => {
                console.error(`Update ${index} failed:`, failure.reason);
            });
        }

        return results;

    } catch (error) {
        console.error('Error in batch afvalstof update:', error);
        throw error;
    }
};



/**
 * Validates afvalstof data before sending to API
 * @param {Object} afvalstofItem - The afvalstof item to validate
 * @returns {Object} - Validation result {isValid: boolean, errors: Array}
 */
export const validateAfvalstofData = (afvalstofItem) => {
    const errors = [];

    if (!afvalstofItem.afvalstofcode) {
        errors.push('Missing afvalstofcode');
    }

    if (afvalstofItem.hoevKg === null || afvalstofItem.hoevKg === undefined || afvalstofItem.hoevKg === '') {
        errors.push('hoevKg is required');
    } else {
        const parsedHoevKg = parseFloat(afvalstofItem.hoevKg);
        if (isNaN(parsedHoevKg)) {
            errors.push('hoevKg must be a valid number');
        } else if (parsedHoevKg < 0) {
            errors.push('hoevKg cannot be negative');
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};


